import { forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

export const badgeVariants = cva(
  [
    // cápsula com tinta suave + texto forte (DESIGN.md › Components › badge)
    "inline-flex items-center gap-1 rounded-pill border border-transparent",
    "font-medium whitespace-nowrap align-middle",
  ],
  {
    variants: {
      tone: {
        neutral: "bg-muted text-muted-foreground",
        success: "bg-success-soft text-success",
        warning: "bg-warning-soft text-warning",
        danger: "bg-danger-soft text-danger",
        info: "bg-info-soft text-info",
        /** Marca: origem Meta Ads, recurso do plano. */
        brand: "bg-primary-soft text-primary-text",
        /** Alias histórico de `brand` (a cor de ação virou o violeta). */
        accent: "bg-primary-soft text-primary-text",
        /** Contagem/rótulo sem semântica de estado. */
        outline: "border-border bg-surface text-foreground",
        /** Em cima de um card `brand` (fundo violeta). */
        "on-brand": "bg-primary-foreground/18 text-primary-foreground",
      },
      /** Badge sólido: use só quando ele PRECISA saltar (contador na sidebar). */
      solid: { true: "", false: "" },
      size: {
        sm: "h-5 px-2 text-2xs",
        lg: "h-6 px-2.5 text-xs",
      },
      /** Etiqueta técnica (código de campanha, UTM): mono, cantos de 6px. */
      mono: {
        true: "rounded-sm border-transparent bg-muted px-1.5 font-mono text-muted-foreground",
        false: "",
      },
    },
    compoundVariants: [
      { tone: "success", solid: true, className: "bg-success text-success-foreground" },
      { tone: "warning", solid: true, className: "bg-warning text-warning-foreground" },
      { tone: "danger", solid: true, className: "bg-danger text-danger-foreground" },
      { tone: "info", solid: true, className: "bg-info text-info-foreground" },
      { tone: "brand", solid: true, className: "bg-primary text-primary-foreground" },
      { tone: "accent", solid: true, className: "bg-primary text-primary-foreground" },
      { tone: "neutral", solid: true, className: "bg-surface-inverse text-inverse-foreground" },
    ],
    defaultVariants: { tone: "neutral", solid: false, size: "sm", mono: false },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>["tone"]>;
export type BadgeSize = NonNullable<VariantProps<typeof badgeVariants>["size"]>;

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Ponto de 6px antes do texto — o segundo canal, além da cor. */
  dot?: boolean;
  /** Ícone de 12px antes do texto (alternativa ao dot). */
  icon?: ReactNode;
  /** Número/contador: liga tabular-nums. */
  numeric?: boolean;
}

/**
 * Cor NUNCA é o único sinal (seção 1.3): todo badge semântico sai com
 * `dot` ou `icon`, e o texto diz o que é.
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, tone, solid, size, mono, dot = false, icon, numeric = false, children, ...props },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(badgeVariants({ tone, solid, size, mono }), numeric && "nums", className)}
      {...props}
    >
      {dot ? (
        <span
          aria-hidden="true"
          className="size-1.5 shrink-0 rounded-full bg-current"
        />
      ) : null}
      {icon ? (
        <span aria-hidden="true" className="inline-flex shrink-0 [&_svg]:size-3">
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
});
